import { useChat } from '../contexts/ChatContext'
import { buildAvatarUrl } from '../services/api'
import { avatarColorIndex } from '@ofself/shell'

/**
 * One renderer for every avatar circle in the app.
 *
 * Priority for the image URL:
 *   1. Live override from the socket (`user_avatar_updated`) — kept
 *      in `userAvatars` on ChatContext. Fires immediately when any
 *      user updates their photo on another device.
 *   2. The URL the server baked into the sender / payload (already
 *      version-keyed for cache-busting).
 *   3. None → render the initials fallback.
 *
 * Props:
 *   userID         — required when the avatar should react to live
 *                    updates. Falls back to sender?.id.
 *   sender         — message Sender shape; gets id, initials, color.
 *   avatarUrl      — explicit override (e.g. when the caller already
 *                    has a relative URL from a non-sender payload).
 *   initials       — fallback text when no sender is provided.
 *   color          — circle background; defaults to deterministic
 *                    palette colour for the name.
 *   size           — px diameter, default 36.
 *   className      — passes through; defaults to "message-avatar"
 *                    to keep CSS classes consistent with existing
 *                    inline avatar markup.
 */
export default function UserAvatar({
  userID,
  sender,
  avatarUrl,
  initials,
  color,
  size,
  className = 'message-avatar',
  style = {},
}) {
  const chat = useChat()
  const userAvatars = chat?.userAvatars || {}

  const id = userID || sender?.id
  const liveOverride = id ? userAvatars[id] : null
  const relativePath = liveOverride ?? avatarUrl ?? sender?.avatar_url ?? null
  const url = buildAvatarUrl(relativePath)

  const name = sender?.username || initials || '?'
  const inits = initials || sender?.initials || (name[0] || '?').toUpperCase()
  // Fallback fill: the caller's color, else a stable hash of the name into the app's
  // --avatar-N palette (the slate-blue family). A real photo (when present) renders as
  // the .avatar-image overlay on top.
  const bg = color || `var(--avatar-${avatarColorIndex(name)})`

  // Inline width/height ONLY when an explicit size is passed. Otherwise
  // let the CSS class own dimensions — chip classes (.reaction-avatar,
  // .thread-indicator-avatar, .typing-avatar) have their own sizing,
  // and forcing 36×36 inline would visually break those clusters.
  const sizeStyle = typeof size === 'number'
    ? { width: size, height: size, minWidth: size }
    : {}

  return (
    <div
      className={className}
      style={{
        ...sizeStyle,
        // Expose ONLY the fill colour; the CSS owns the paint (a soft 15% wash +
        // same-colour initials). We deliberately do NOT set a solid `background`
        // here — a solid inline fill is what regressed the avatars to flat discs
        // and forced the theme to fight it with !important. CSS wins cleanly now.
        '--av-color': bg,
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      <span className="avatar-initials">{inits}</span>
      {url && (
        <img
          src={url}
          alt=""
          className="avatar-image"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      )}
    </div>
  )
}
